import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import { useClearSession } from '../api/session.js'
import type { ObjectContext, TaskStatus } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { Wordmark } from '../components/Logo.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Field, Notice, Spinner, StatusBadge } from '../components/ui.js'
import { addDays, addMonths, endOfMonth, formatDate, formatDateTime, formatFileSize, formatWeekday, startOfMonth, todayIso } from '../lib/format.js'

// ---------------------------------------------------------------- Typen

interface PortalSiteCard {
  id: string
  name: string
  street: string | null
  zip: string | null
  city: string | null
  next: { title: string; dueDate: string; place: string }[]
  last: { title: string; dueDate: string; completedAt: string | null; place: string } | null
  done30: number
  missed30: number
  openIssues: number
}

export interface PortalOverview {
  tenantName: string
  customerName: string
  user: { name: string; email: string | null }
  sites: PortalSiteCard[]
}

interface PortalTask extends ObjectContext {
  id: string
  title: string
  status: TaskStatus
  dueDate: string
  dueTime: string | null
  textOption: string | null
  finalReason: string | null
  completedAt: string | null
}

interface PortalSiteDetail {
  site: { id: string; name: string; number: string | null; street: string | null; zip: string | null; city: string | null }
  plans: (ObjectContext & { id: string; title: string; rhythm: string; season: string | null; next: string | null })[]
  upcoming: PortalTask[]
  history: PortalTask[]
  defects: (ObjectContext & { id: string; title: string; status: string; createdAt: string })[]
}

export function usePortalOverview() {
  return useQuery({ queryKey: ['portal-overview'], queryFn: () => api<PortalOverview>('/api/portal/overview') })
}

function address(site: { street: string | null; zip: string | null; city: string | null }): string {
  return [site.street, [site.zip, site.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
}

// ---------------------------------------------------------------- Rahmen

/**
 * Kundenportal: the owner looks in on their Liegenschaften. Deliberately calm
 * and read-only; the one thing to do here is to file an Anliegen.
 */
export function PortalLayout() {
  const overview = usePortalOverview()
  const clearSession = useClearSession()
  const navigate = useNavigate()
  return (
    <div className="portal">
      <header className="portal-head">
        <div className="portal-head-in">
          <Wordmark size={30} tone="light" subtitle={overview.data ? `Kundenportal ${overview.data.tenantName}` : 'Kundenportal'} />
          <nav className="portal-nav" aria-label="Portal">
            <NavLink to="/portal" end>
              Übersicht
            </NavLink>
            <NavLink to="/portal/tickets">Anliegen</NavLink>
            <NavLink to="/portal/konto">Konto</NavLink>
          </nav>
          <button
            type="button"
            className="btn ghost small portal-logout"
            aria-label="Abmelden"
            onClick={async () => {
              await clearSession()
              navigate('/login', { replace: true })
            }}
          >
            <Icon name="logout" size={16} /> <span className="portal-logout-label">Abmelden</span>
          </button>
        </div>
      </header>
      <main className="portal-main">
        <Outlet />
      </main>
    </div>
  )
}

function PortalTitle({ title, subtitle, action }: { title: string; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div className="portal-title">
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="muted">{subtitle}</div>}
      </div>
      {action}
    </div>
  )
}

// ---------------------------------------------------------------- Übersicht

export function PortalOverviewPage() {
  const { data, isLoading, error } = usePortalOverview()
  if (isLoading) return <Spinner />
  if (error || !data) return <Notice kind="error">{error instanceof ApiError ? error.message : 'Konnte nicht geladen werden.'}</Notice>
  return (
    <>
      <PortalTitle
        title={data.customerName || 'Ihre Liegenschaften'}
        subtitle={`Angemeldet als ${data.user.name}. Was erledigt wurde, was geplant ist, und Ihre Anliegen.`}
        action={
          <Link className="btn" to="/portal/tickets/neu">
            <Icon name="plus" /> Anliegen melden
          </Link>
        }
      />
      {data.sites.length === 0 ? (
        <section className="card">
          <EmptyState title="Noch keine Liegenschaften freigeschaltet" hint="Bitte wenden Sie sich an Ihre Hausverwaltung." />
        </section>
      ) : (
        <div className="portal-grid">
          {data.sites.map((site) => {
            const total = site.done30 + site.missed30
            return (
              <Link key={site.id} to={`/portal/liegenschaft/${site.id}`} className="card portal-site">
                <div className="card-body">
                  <div className="portal-site-head">
                    <div>
                      <div className="strong big">{site.name}</div>
                      <div className="muted small">{address(site)}</div>
                    </div>
                    {total > 0 && (
                      <div className="portal-rate" title="Erledigt in den letzten 30 Tagen">
                        <span className="strong">{Math.round((site.done30 / total) * 100)} %</span>
                        <span className="muted small">erledigt, 30 Tage</span>
                      </div>
                    )}
                  </div>
                  <dl className="portal-facts">
                    <dt>Zuletzt</dt>
                    <dd>
                      {site.last ? (
                        <>
                          {site.last.title}, {formatDate(site.last.dueDate)}
                          <span className="muted small"> {site.last.place}</span>
                        </>
                      ) : (
                        <span className="muted">noch nichts</span>
                      )}
                    </dd>
                    <dt>Als Nächstes</dt>
                    <dd>
                      {site.next.length > 0 ? (
                        site.next.map((n, i) => (
                          <div key={i}>
                            {formatWeekday(n.dueDate)} {formatDate(n.dueDate)}: {n.title}
                            <span className="muted small"> {n.place}</span>
                          </div>
                        ))
                      ) : (
                        <span className="muted">nichts geplant</span>
                      )}
                    </dd>
                    {site.openIssues > 0 && (
                      <>
                        <dt>Offen</dt>
                        <dd>
                          {site.openIssues} {site.openIssues === 1 ? 'Mangel oder Anliegen' : 'Mängel oder Anliegen'}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------- Liegenschaft

type SiteTab = 'plans' | 'history' | 'upcoming' | 'defects' | 'documents' | 'proof'

export function PortalSitePage() {
  const { id } = useParams<{ id: string }>()
  const [tab, setTab] = useState<SiteTab>('history')
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-site', id],
    queryFn: () => api<PortalSiteDetail>(`/api/portal/sites/${id}`),
    enabled: Boolean(id),
  })
  if (isLoading) return <Spinner />
  if (error || !data) return <Notice kind="error">{error instanceof ApiError ? error.message : 'Konnte nicht geladen werden.'}</Notice>

  const tabs: { key: SiteTab; label: string; count?: number }[] = [
    { key: 'history', label: 'Erledigt', count: data.history.filter((h) => h.status === 'done').length },
    { key: 'upcoming', label: 'Geplant', count: data.upcoming.length },
    { key: 'plans', label: 'Leistungen', count: data.plans.length },
    { key: 'defects', label: 'Mängel', count: data.defects.length },
    { key: 'documents', label: 'Dokumente' },
    { key: 'proof', label: 'Nachweis' },
  ]

  return (
    <>
      <div className="muted small" style={{ marginBottom: 6 }}>
        <Link to="/portal">Übersicht</Link> › {data.site.name}
      </div>
      <PortalTitle
        title={data.site.name}
        subtitle={address(data.site)}
        action={
          <Link className="btn" to={`/portal/tickets/neu?liegenschaft=${data.site.id}`}>
            <Icon name="plus" /> Anliegen melden
          </Link>
        }
      />
      <div className="tabs" role="tablist">
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>
            {t.label}
            {t.count !== undefined && <span className="n">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'history' && <TaskTable tasks={data.history} empty="In den letzten 60 Tagen wurde hier nichts erfasst." showResult />}
      {tab === 'upcoming' && <TaskTable tasks={data.upcoming} empty="In den nächsten vier Wochen ist nichts geplant." />}
      {tab === 'plans' && (
        <section className="card">
          {data.plans.length === 0 ? (
            <EmptyState title="Keine laufenden Leistungen" />
          ) : (
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Leistung</th>
                    <th>Wo</th>
                    <th>Rhythmus</th>
                    <th className="nowrap">Nächster Termin</th>
                  </tr>
                </thead>
                <tbody>
                  {data.plans.map((p) => (
                    <tr key={p.id}>
                      <td className="strong">{p.title}</td>
                      <td>{p.path.length ? p.path.join(' › ') : 'gesamte Liegenschaft'}</td>
                      <td>
                        {p.rhythm}
                        {p.season && <div className="muted small">{p.season}</div>}
                      </td>
                      <td className="nowrap">{p.next ? `${formatWeekday(p.next)} ${formatDate(p.next)}` : <span className="muted">–</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      {tab === 'defects' && (
        <section className="card">
          {data.defects.length === 0 ? (
            <EmptyState title="Keine offenen Mängel" hint="Gemeldete Mängel erscheinen hier, bis sie behoben sind." />
          ) : (
            <ul className="feed">
              {data.defects.map((d) => (
                <li key={d.id}>
                  <StatusBadge status={d.status} />
                  <div>
                    <div className="strong">{d.title}</div>
                    <div className="muted small">
                      {d.label}, gemeldet {formatDateTime(d.createdAt)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {tab === 'documents' && <PortalDocuments siteId={data.site.id} />}
      {tab === 'proof' && <ProofBox siteId={data.site.id} />}
    </>
  )
}

interface PortalDocument extends ObjectContext {
  id: string
  objectId: string
  name: string
  bytes: number
  createdAt: string
}

/** Only what the office explicitly shared; everything else stays internal. */
function PortalDocuments({ siteId }: { siteId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['portal-documents', siteId],
    queryFn: () => api<PortalDocument[]>(`/api/portal/sites/${siteId}/documents`),
  })
  return (
    <section className="card">
      {isLoading ? (
        <Spinner />
      ) : !data || data.length === 0 ? (
        <EmptyState title="Keine Dokumente freigegeben" hint="Hier erscheinen z. B. Leistungsverzeichnis oder Reinigungsplan, sobald wir sie freigeben." />
      ) : (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Dokument</th>
                <th>Wo</th>
                <th className="nowrap">Stand</th>
              </tr>
            </thead>
            <tbody>
              {data.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <a href={`/api/documents/${doc.id}`} target="_blank" rel="noreferrer" className="strong">
                      <Icon name="file" size={14} /> {doc.name}
                    </a>
                    <div className="muted small">{formatFileSize(doc.bytes)}</div>
                  </td>
                  <td className="small">{doc.path.join(' › ') || 'Liegenschaft'}</td>
                  <td className="nowrap small">{formatDate(doc.createdAt.slice(0, 10))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function TaskTable({ tasks, empty, showResult }: { tasks: PortalTask[]; empty: string; showResult?: boolean }) {
  if (tasks.length === 0) {
    return (
      <section className="card">
        <EmptyState title={empty} />
      </section>
    )
  }
  return (
    <section className="card">
      <div className="table-scroll">
        <table className="data">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Leistung</th>
              <th>Wo</th>
              {showResult && <th>Ergebnis</th>}
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id}>
                <td className="nowrap">
                  {formatDate(t.dueDate)}
                  <div className="muted small">{formatWeekday(t.dueDate)}</div>
                </td>
                <td className="strong">{t.title}</td>
                <td>{t.path.length ? t.path.join(' › ') : 'gesamte Liegenschaft'}</td>
                {showResult && (
                  <td>
                    <StatusBadge status={t.status} />
                    {t.status === 'done' && t.textOption && <div className="small">{t.textOption}</div>}
                    {t.status === 'final_missed' && t.finalReason && <div className="muted small">{t.finalReason}</div>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function ProofBox({ siteId }: { siteId: string }) {
  const first = startOfMonth(addMonths(todayIso(), -1))
  const [from, setFrom] = useState(first)
  const [to, setTo] = useState(endOfMonth(first))
  const url = `/api/portal/sites/${siteId}/proof?from=${from}&to=${to}`
  return (
    <section className="card">
      <div className="card-body">
        <p style={{ marginTop: 0 }}>
          Der Leistungsnachweis zeigt je Leistung, was wann erledigt wurde, mit Fotos. Nicht Erledigtes steht mit Grund darin.
        </p>
        <div className="row">
          <Field label="Von">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Bis">
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
        <div className="actions">
          <a className="btn" href={url} target="_blank" rel="noreferrer">
            <Icon name="download" /> Nachweis öffnen
          </a>
          <button type="button" className="btn secondary" onClick={() => { setFrom(addDays(todayIso(), -29)); setTo(todayIso()) }}>
            Letzte 30 Tage
          </button>
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------- Konto

export function PortalAccountPage() {
  const toast = useToast()
  const overview = usePortalOverview()
  const [error, setError] = useState<string | null>(null)
  const change = useMutation({
    mutationFn: (body: { current: string; next: string }) => api('/api/portal/password', { method: 'POST', body }),
    onSuccess: () => {
      setError(null)
      toast('Passwort geändert.')
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Ändern fehlgeschlagen.'),
  })
  return (
    <>
      <PortalTitle title="Konto" subtitle={overview.data ? `${overview.data.user.name}, ${overview.data.user.email ?? ''}` : undefined} />
      <section className="card" style={{ maxWidth: 520 }}>
        <div className="card-head">
          <h2>Passwort ändern</h2>
        </div>
        <div className="card-body">
          {error && <Notice kind="error">{error}</Notice>}
          <form
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault()
              const form = new FormData(event.currentTarget)
              if (form.get('next') !== form.get('repeat')) {
                setError('Die beiden neuen Passwörter stimmen nicht überein.')
                return
              }
              change.mutate({ current: String(form.get('current')), next: String(form.get('next')) })
              event.currentTarget.reset()
            }}
          >
            <Field label="Bisheriges Passwort">
              <input name="current" type="password" autoComplete="current-password" required />
            </Field>
            <Field label="Neues Passwort" hint="Mindestens 10 Zeichen.">
              <input name="next" type="password" autoComplete="new-password" minLength={10} required />
            </Field>
            <Field label="Neues Passwort wiederholen">
              <input name="repeat" type="password" autoComplete="new-password" minLength={10} required />
            </Field>
            <button type="submit" className="btn" disabled={change.isPending}>
              Passwort ändern
            </button>
          </form>
        </div>
      </section>
    </>
  )
}

// ---------------------------------------------------------------- Einladung

export function PortalInvitePage() {
  const { token } = useParams<{ token: string }>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const info = useQuery({
    queryKey: ['portal-invite', token],
    queryFn: () => api<{ name: string; email: string; role: string; tenantName: string }>(`/api/auth/portal-invite/${token}`),
    retry: false,
  })
  const redeem = useMutation({
    mutationFn: (password: string) => api('/api/auth/portal-invite', { method: 'POST', body: { token, password } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      // The app sends everyone to their own start page from there.
      navigate(info.data?.role === 'kunde' ? '/portal' : '/', { replace: true })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.'),
  })

  return (
    <div className="center-page">
      <div className="box">
        <div className="card">
          <div className="card-body">
            <div style={{ marginBottom: 22 }}>
              <Wordmark size={34} subtitle={!info.data || info.data.role === 'kunde' ? 'Kundenportal' : 'Verwaltung'} />
            </div>
            {info.isLoading ? (
              <Spinner />
            ) : info.error ? (
              <Notice kind="error">{info.error instanceof ApiError ? info.error.message : 'Diese Einladung ist nicht mehr gültig.'}</Notice>
            ) : (
              <>
                <p style={{ marginTop: 0 }}>
                  Guten Tag {info.data!.name}, {info.data!.tenantName} hat für Sie einen Zugang
                  {info.data!.role === 'kunde' ? ' zum Kundenportal' : ' zur Verwaltung'} eingerichtet. Bitte wählen Sie ein Passwort.
                </p>
                {error && <Notice kind="error">{error}</Notice>}
                <form
                  onSubmit={(event: FormEvent<HTMLFormElement>) => {
                    event.preventDefault()
                    const form = new FormData(event.currentTarget)
                    if (form.get('password') !== form.get('repeat')) {
                      setError('Die beiden Passwörter stimmen nicht überein.')
                      return
                    }
                    redeem.mutate(String(form.get('password')))
                  }}
                >
                  <Field label="E-Mail-Adresse">
                    <input value={info.data!.email} readOnly autoComplete="username" />
                  </Field>
                  <Field label="Passwort" hint="Mindestens 10 Zeichen.">
                    <input name="password" type="password" minLength={10} autoComplete="new-password" required autoFocus />
                  </Field>
                  <Field label="Passwort wiederholen">
                    <input name="repeat" type="password" minLength={10} autoComplete="new-password" required />
                  </Field>
                  <button type="submit" className="btn block" disabled={redeem.isPending}>
                    Zugang einrichten
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
